/**
 * mock 서버의 상태. handler 는 응답을 즉석에서 만들지 말고 여기를 읽고 고친다 —
 * 그래야 POST/PATCH 뒤에 목록을 다시 받으면 바뀐 값이 보인다.
 *
 * 원천은 Notion 스키마의 테이블들이다. 화면이 받는 모양(Me, 프로젝트 상세, Weekly …)은
 * 여기 따로 두지 않고 handler 와 ./derive.ts 가 계산한다.
 *
 * 세션만 쿠키에 둔다. 새로고침할 때마다 로그아웃되면 화면 작업이 번거롭다.
 * 나머지 데이터는 새로고침하면 시드로 돌아간다. 로그아웃 → vite.config.ts 의 mockApi
 */
import type { AttendanceAttachment, DailyEditRequest, Leave, Me, WeeklyComment } from "@/types";
import { assistantConversations, assistantMessages } from "./data/assistant";
import {
  scheduleCategories,
  schedules,
  vacationGrants,
  vacationKinds,
  vacations,
} from "./data/attendance";
import { holidays } from "./data/holidays";
import { dailies, dailyCategories } from "./data/dailies";
import { dailyComments, dailyEditRequests, dailyImages } from "./data/dailyExtras";
import { notifications } from "./data/notifications";
import { billings, contracts, incomes, outsourcings } from "./data/money";
import {
  detailFields,
  projectDetails,
  projectMemberPlans,
  projectMembers,
  projectPhases,
  projects,
} from "./data/projects";
import { siteConfig, userNotificationSettings } from "./data/settings";
import { site } from "./data/site";
import { salaries, staff, staffFields } from "./data/staff";
import { seedWeeklyComments } from "./data/weeklyStory";
import * as documentsSeed from "./data/documents";
import { toMe } from "./derive";
import { MOCK_SESSION_COOKIE } from "./session";

/** 세션이 바뀌면 window 에 쏘는 이벤트. → ./DevFloatingTool.tsx */
export const SESSION_CHANGED = "mock:session-changed";

function readSession(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${MOCK_SESSION_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export const db = {
  /* 엔티티 — 고쳐 쓰는 테이블 */
  users: structuredClone(staff),
  /** 기간별 급여 — 덮어쓰지 않고 쌓는다. 새로 넣으면 앞 기간이 그 날짜에서 닫힌다 */
  salaries: structuredClone(salaries),
  /** 휴직 기록 — 쌓기만 한다. 재직 상태의 휴직 · 재직은 이 기록을 따른다 */
  leaves: [] as (Leave & { user_id: string })[],
  /** 직원 항목 정의 — 값은 users[].fields 에 있다 */
  staffFields: structuredClone(staffFields),
  projects: structuredClone(projects),
  projectPhases: structuredClone(projectPhases),
  projectMembers: structuredClone(projectMembers),
  /** 기간별 투입 계획 — Process 의 배치 캡슐 하나가 이 한 행이다 */
  projectMemberPlans: structuredClone(projectMemberPlans),
  /** Ask AI 의 지난 대화와 메시지 — 목은 모델을 부르지 않고 답을 지어낸다 */
  assistantConversations: structuredClone(assistantConversations),
  assistantMessages: structuredClone(assistantMessages),
  /** 상세 항목 정의와 값 — 용도·면적·공사비가 모두 여기 들어간다 */
  detailFields: structuredClone(detailFields),
  projectDetails: structuredClone(projectDetails),
  /** 금액 — 계약 금액은 개정 이력에서 그 시점 값을 뽑는다 */
  contracts: structuredClone(contracts),
  billings: structuredClone(billings),
  incomes: structuredClone(incomes),
  outsourcings: structuredClone(outsourcings),
  /** 휴가 신청 — 승인, 반려, 취소는 status 를 고쳐 쓴다 */
  vacations: structuredClone(vacations),
  /** 근무 신청 — 휴가처럼 결재를 받는다. 취소해도 행은 남고 status 만 바뀐다 */
  schedules: structuredClone(schedules),
  /** 근무 유형 — 사무소가 늘린다 */
  scheduleCategories: structuredClone(scheduleCategories),
  /** 휴가 유형 — 사무소가 늘리고 규칙을 고친다 */
  vacationKinds: structuredClone(vacationKinds),
  /** 근태 첨부파일 — 파일은 NAS 대신 바이트를 들고 있는다. 신청에 붙으면 owner 가 채워진다 */
  attendanceAttachments: [] as (AttendanceAttachment & {
    uploaded_by: string;
    owner: string | null;
    data: ArrayBuffer;
  })[],
  /** 휴가 유형 — 사무소가 사내 규정에 맞게 늘린다 */
  /** 휴일 — 하루에 한 행 */
  holidays: structuredClone(holidays),
  /** 연차 부여 — 사용과 잔여는 handler 가 휴가에서 센다 */
  vacationGrants: structuredClone(vacationGrants),
  site: structuredClone(site),
  /** 회사 설정 — 사무소 하나당 한 행 */
  siteConfig: structuredClone(siteConfig),
  /** 사람마다 끈 알림 — 계정에 묶인다 */
  userNotificationSettings: structuredClone(userNotificationSettings),
  /** 내 알림 — 사람마다 쌓인다 */
  notifications: structuredClone(notifications),
  documents: structuredClone({
    folders: documentsSeed.folders,
    documents: documentsSeed.documents,
  }),
  /** Weekly 프로젝트 보고에 달린 코멘트 — 주(월요일)와 프로젝트에 묶인다 */
  weeklyComments: seedWeeklyComments({
    dailies,
    projects,
    projectPhases,
    users: staff,
  }) as WeeklyComment[],
  /** Daily — 하루 한 행(daily_table)과 그 안의 업무 줄(daily_row) */
  dailies: structuredClone(dailies),
  dailyCategories: structuredClone(dailyCategories),
  /** Daily 수정 요청 — 승인하면 그때 줄에 반영한다 */
  dailyEditRequests: structuredClone(dailyEditRequests) as DailyEditRequest[],
  /** Daily 코멘트 — 값과 집계를 바꾸지 않는다 */
  dailyComments: structuredClone(dailyComments),
  /** Daily 사진 — 파일 바이트는 mock 에 두지 않고 data URL 로 들고 있는다 */
  dailyImages: structuredClone(dailyImages),
  /** 사람마다 바꾼 비밀번호 — 없으면 공용 MOCK_PASSWORD. 새로고침하면 처음으로 돌아간다. */
  passwords: {} as Record<string, string>,

  currentUser(): Me | undefined {
    const id = readSession();
    const user = id ? this.users.find((u) => u.id === id) : undefined;
    return user && toMe(user);
  },

  signIn(userId: string) {
    document.cookie = `${MOCK_SESSION_COOKIE}=${encodeURIComponent(userId)}; Path=/; SameSite=Lax`;
    // 로그인 폼으로 들어온 경우에도 개발용 도구가 지금 계정을 따라가게.
    window.dispatchEvent(new Event(SESSION_CHANGED));
  },
};
